import {
	DialogHandler,
	FullPageLoaderHandler,
} from "cakemail-ui-components/lib/react";
import { I18n, Translate } from "react-redux-i18n";
import React from "react";
import * as moment from "moment-timezone";
import store from "../store";
import { setUsageData } from "assets/js/data/usage/actions";
import { getAccountStatsService } from "assets/js/services/accountService";
import {
	checkLimitNotification,
	openLimitDialog,
} from "utils/accountNotification";
import fetchRoute from "./routes";
import {
	analyticsLogout,
	SEGMENT_EVENTS,
	trackSegmentEvent,
} from "assets/js/const/SegmentEvents";
import {
	acceptListPolicyService,
	createListService,
} from "assets/js/services/listService";
import { listSendersService } from "assets/js/services/SenderService";
import {
	showZendeskSupportDialog, SUPPORT_TYPE,
} from "assets/js/components/generalDialogs/simpleDialogs";
import { isValidEmail } from "utils/validators";
import { listUsersService } from "assets/js/services/userService";
import { clearGlobalItems } from "utils/componentUtils";
import { setAccountReady, setAuthenticated } from "assets/js/actions";
import { getUrlsBasedOnLanguage } from "utils/languages";
import { getLocalStorage, setLocalStorage } from "./localStorageUtil";
import { ERROR_TYPES, logError } from "./sentry";
import Cookies from "universal-cookie";
export const APP_URL = window.location.origin;
export const GATEWAY_PROXY = APP_URL + "/api";

export const handleCustomApiFailure = (
	response,
	failureCallback = () => { },
	errorCode = null,
	payload = null
) => {
	const details =
		response?.body?.detail || response?.detail || response?.body?.error?.detail;
	let usage = null;
	let limits = null;

	if (details?.[0] && details[0]?.code) {
		const code = errorCode ? errorCode : details[0].code;
		switch (code) {
			case 4:
				if (
					!window.location.href.includes(fetchRoute("public")) &&
					!window.location.href.includes("/auth")
				) {
					globalLogOut();
					window.location = window.location.search + "#" + fetchRoute("signIn");
				}
				failureCallback("app-general-error-code-401", response, code);
				break;
			case 403:
				failureCallback("app-general-error-code-403", response, code);
				break;
			//Rate limit exceeded
			case 429:
				failureCallback("app-general-error-code-429", response, code);
				break;
			//603 => Signup Disabled
			case 603:
				failureCallback("app-general-error-code-603", response, code);
				break;
			//1000 => Not enough permissions
			case 1000:
				DialogHandler.show({
					type: "error",
					title: <Translate value="app-general-error-code-1000" />,
				});
				break;
			//1001 => User pending confirmation
			case 1001:
				failureCallback(null, response, code);
				break;
			//1002 => Invalid credentials
			case 1002:
				failureCallback("app-auth-sign_error_incorrect", response, code);
				break;
			//1003 => Invalid token
			case 1003:
				failureCallback("app-general-error-code-1003", response, code);
				break;
			//1501 => Form policy not compatible with list current trigger configuration
			case 1501:
				DialogHandler.show({
					type: "warning",
					title: <Translate value="app-forms-policies-legacy-issue-title" />,
					content: (
						<p>
							<Translate value="app-forms-policies-legacy-issue-text" />
						</p>
					),
					primaryBtnText: <Translate value="app-general-close" />,
				});
				failureCallback(null, response, code);
				break;
			//2010, password change needs to be different form previous one
			case 2010:
				failureCallback("app-general-error-code-2010", response, code);
				break;
			//1007 => User account is suspended
			case 1007:
				trackSegmentEvent(SEGMENT_EVENTS.ACCOUNT_SUSPENDED, { email: "" });
				window.location =
					window.location.search + "#" + fetchRoute("errorFallback");
				break;
			//1006 => User account is locked out
			case 1006:
				failureCallback("app-general-error-code-1006", response, code);
				break;
			case 2035:
				failureCallback("app-general-error-code-2035", response, code);
				break;
			//2051 There are too many suppressed emails to export
			case 2051:
				// eslint-disable-next-line no-case-declarations
				const message = `User is trying to export his suppression list but has more then the allowed api limit of 25 000 entries.
			
			URL : ${window.location.href}
			`;
				showZendeskSupportDialog(SUPPORT_TYPE.SUPPORT, message, { text: "app-suppressed-emails-export-to-big-text" });
				failureCallback(null, response, code);
				break;
			//Failed MFA code
			case 2056:
				failureCallback("app-auth-sign-in-challenge-code-fail", response, code);
				break;
			case 9200:
				// Action is blocked due to low score
				trackSegmentEvent(SEGMENT_EVENTS.ACTION_BLOCKED);
				failureCallback("app-general_action-blocked", response, code);
				break;
			case 8004:
				DialogHandler.show({
					type: "warning",
					title: <Translate value="app-general-error-code-8004" />
				});
				failureCallback(null, response, code);
				break;
			case 8008:
			case 8006:
				logError(`Error code ${code}`, {
					url: payload?.url,
					details
				});
				// No senders, or sender not confirmed
				failureCallback("app-general_no-sender", response, code);
				break;
			case 9000:
				// Internnal comunication error
				failureCallback("app-general-error-general", response, code);
				break;
			default:
				failureCallback(null, response, code);
				break;
		}
	} else {
		const message = typeof details === "string" ? details : details?.[0]?.msg;

		switch (message) {
			case "There is already a pending account at this email address":
				failureCallback(null, response, 1001);
				break;
			case "Not authenticated":
				if (
					!window.location.href.includes(fetchRoute("public")) &&
					!window.location.href.includes("/auth")
				) {
					globalLogOut();
					window.location = window.location.search + "#" + fetchRoute("signIn");
				}
				break;
			case "Account address information is required":
				failureCallback("app-general-error-address-missing", response, 422);
				break;
			case "Metadata not found":
			case "Campaign id not found":
				failureCallback(null, response, 404);
				break;
			case "Metadata point already exists":
				failureCallback(null, response, 422);
				break;
			case "Contact limit exceeded":
				usage = store.getState().global.usage;
				limits = {
					contactLimit:
						store.getState().global.account.usage_limits.maximum_contacts,
					emailsMonthlyLimits:
						store.getState().global.account.usage_limits.per_month,
				};

				openLimitDialog(usage, limits);

				failureCallback(null, response, 400, { handled: true });
				break;
			case "Campaign exceeds the per-month emails limit":
			case "Campaign exceeds the per-campaign emails limit":
				usage = store.getState().global.usage;
				limits = {
					contactLimit:
						store.getState().global.account.usage_limits.maximum_contacts,
					emailsMonthlyLimits:
						store.getState().global.account.usage_limits.per_month,
				};

				openLimitDialog(
					usage,
					limits,
					<Translate
						value="app-billing-limit-sent-usage-campaign-desc"
						dangerousHTML
					/>,
					[
						{
							desc: (
								<Translate value="app-billing-limit-sent-usage-campaign-usage" />
							),
							info: usage.emails,
						},
						{
							desc: (
								<Translate value="app-billing-limit-sent-usage-campaign-limit" />
							),
							info: limits.emailsMonthlyLimits,
						},
					],
					<Translate
						value="app-billing-limit-sent-usage-campaign-title"
						usage={Math.floor(usage.emailUsage)}
					/>
				);

				failureCallback(null, response, 400, { handled: true });
				break;
			case "Custom attribute is in use":
				failureCallback("app-list-attribute-error-in_use", response, 400, {
					bigMessage: true,
				});
				break;
			default:
				//Not exact message
				if (message) {
					if (message.includes("is already taken!")) {
						failureCallback("app-auth-create_error_2_general", response);
					} else if (message.includes("does not have a subscription.")) {
						failureCallback("no subscription", response, null);
					} else if (message.includes("is a read-only attribute")) {
						failureCallback(
							"app-list-attribute-error-system-field",
							response,
							null
						);
					} else {
						failureCallback(null, response);
					}
				} else {
					failureCallback(null, response);
				}
				break;
		}
	}
};

export function globalLogOut(redirection = false) {
	store.dispatch({ type: "RESET_STORE" });
	store.dispatch(setAuthenticated(false));
	store.dispatch(setAccountReady(false));

	const cookies = new Cookies();
	cookies.remove("connect.sid");

	const user = store.getState().global.user;

	trackSegmentEvent(SEGMENT_EVENTS.SIGN_OUT, user);

	analyticsLogout();

	clearGlobalItems();
}

export function getAccountUsage(
	isUpdate = true,
	accountId,
	prevLimits = null,
	callback = () => { }
) {
	const storeUsage = store.getState().global.usage;
	const account = store.getState().global.account;

	getAccountStatsService(
		{},
		(data) => {
			let curUsage = { ...storeUsage };

			curUsage.emails = data.data.sent_emails;
			curUsage.emailUsage = data.data.emails_usage;
			curUsage.contacts = data.data.active_contacts;
			curUsage.contactUsage = data.data.contacts_usage;
			curUsage.currentLists = data.data.current_lists;
			curUsage.currentUsers = -1;
			curUsage.emailSubmissions = data.data.email_api?.submitted || 0;
			// Because the new app works with modals and not the old one, 
			// we need to store the full report that would have been fetched be default otherwise
			curUsage.fullReport = data.data;

			//If the fetch is not an update, so a fresh page load, the oldUsage is the currentUsage
			let oldUsage = null;
			if (!isUpdate) {
				callback({ limits: account.usage_limits, usage: curUsage });
				oldUsage = curUsage;
			} else {
				oldUsage = { ...storeUsage };
			}

			//Add extra layer of usage like this until stats call has all that is needed
			listUsersService(
				{ withCount: true, perPage: 1 },
				(data) => {
					curUsage.currentUsers = data.pagination.count;
					store.dispatch(setUsageData({ ...curUsage }));
				},
				() => {
					store.dispatch(setUsageData({ ...curUsage }));
				}
			);

			checkLimitNotification(oldUsage, curUsage, isUpdate);
		},
		() => {
			callback({});
		}
	);
}

export function openBillingPortal(
	btnRef = null,
	priceId = null,
) {
	if (isPartner(true, { btnRef: btnRef })) {
		return;
	}
	if (store.getState().brand?.client_config?.intercom_classname) {
		DialogHandler.hide();
		return;
	}
	const stripeAccess =
		store.getState().brand?.config?.stripe_portal_access === true;
	let message = "User wants to upgrade, but the portal failed to open. \n";
	const billingPlanURL = getUrlsBasedOnLanguage(
		store.getState().brand?.url?.plan_upgrade,
		"plan_upgrade",
		store.getState().global.language
	);

	if (stripeAccess) {
		window.location = "#" + fetchRoute("billing");
	}
	else if (billingPlanURL) {
		window.location = billingPlanURL;
	} else {
		showZendeskSupportDialog("upgrade", message);
		FullPageLoaderHandler.hide();
	}

	if (btnRef?.current) {
		btnRef.current.reset();
	}

}

export function getBrandId() {
	const id = store.getState().brand?.config?.brand_id;
	return id ? id : "default";
}

export function getUpgradeButtonClassName() {
	return store.getState().brand?.client_config?.intercom_classname || "";
}

export const METHODS = {
	post: "POST",
	put: "PUT",
	delete: "DELETE",
	get: "GET",
	patch: "PATCH",
};

export const DEFAULT_LIST_PARAMS = {
	page: 1,
	perPage: 25,
	withCount: false,
	filter: null,
	sort: null,
};

export function callAPI(
	{
		url = "",
		method = METHODS.post,
		body = null,
		query = {},
		headers = null,
		skipRetry = false,
		useImpersonationTree = true,
		mode = null,
		skipSessionValidation = false
	},
	successCallback = () => { },
	failureCallback = () => { }
) {
	const sessionId = store.getState().global?.sessionId;
	const options = prepareMicroServiceCall(
		method || METHODS.post,
		body,
		headers,
		mode
	);

	let queryExtention = "";
	const initQueryChar = url.includes("?") ? "&" : "?";

	const impersonationTree = getImpersonificationTree();
	if (useImpersonationTree && impersonationTree && Array.isArray(impersonationTree) && impersonationTree?.length > 0) {
		const urlPathname = new URL(url).pathname;
		const impersonationIndex = impersonationTree[impersonationTree.length - 1];
		if (urlPathname === "/api/accounts") {
			query.partner_account_id = impersonationIndex;
		} else if (urlPathname.includes("/api/workflows") || urlPathname.includes("/api/automations") || urlPathname.includes("/api/customevents") || urlPathname.includes("/api/domains")) {
			query.accountId = impersonationIndex;
		} else {
			query.account_id = impersonationIndex;
		}
	}

	for (const key in query) {
		if (![null, undefined].includes(query[key])) {
			const data =
				typeof query[key] === "object"
					? JSON.stringify(query[key])
					: query[key];
			queryExtention += `&${key}=${encodeURIComponent(data)}`;
		}
	}

	if (queryExtention) {
		queryExtention = initQueryChar + queryExtention.slice(1);
	}

	url = url + queryExtention;

	function validateResponse(response, result) {
		if (response != null && response.status >= 200 && response.status < 400) {
			if (successCallback) {
				successCallback(result);
			}
		} else {
			handleCustomApiFailure(
				{ ...response, body: result, status: response.status },
				failureCallback,
				null,
				url
			);
		}
	}

	function retry(tries = 0, skipRetry) {
		fetch(url, options)
			.then((response) => {
				if (!skipSessionValidation && isNotSameSession(sessionId)) {
					console.log("Failing call then", sessionId, url, options);
					return;
				}

				let data = null;
				if (response.headers.get("content-type").includes("application/json")) {
					data = response.json();
				} else {
					data = response.text();
				}

				data
					.then((result) => {
						if (
							!skipRetry &&
							[500, 502, 503, 504].includes(response.status) &&
							method === METHODS.get &&
							tries < 2
						) {
							retry((tries += 1));
						} else {
							validateResponse(response, result);
						}
					})
					.catch((error) => {
						handleCustomApiFailure(error, failureCallback, null, url);
					});
			})
			.catch((error) => {
				if (!skipSessionValidation && isNotSameSession(sessionId)) {
					console.log("failing call", sessionId, url, options);
					return;
				}

				if (!skipRetry && tries < 2) {
					retry((tries += 1));
				} else {
					handleCustomApiFailure(error, failureCallback, null, url);
				}
			});
	}
	retry(0, skipRetry);
}

function isNotSameSession(expectedSessionId) {
	const { account, user, sessionId } = store.getState().global;
	if (expectedSessionId !== sessionId) {
		logError(ERROR_TYPES.UnexpectedSessionIdException, {
			accountId: account?.id,
			userId: user?.id,
			userEmail: user?.email,
			pageUrl: window.location.href,
			expectedSessionId,
			currentSessionId: sessionId
		});
	}

	return expectedSessionId !== sessionId;
}

export function prepareMicroServiceCall(
	method = METHODS.post,
	data = null,
	headers = null,
	mode = null
) {
	const _headers = new Headers();
	_headers.set("content-type", "application/json");
	if (headers) {
		for (const headerKey in headers) {
			_headers.set(headerKey, headers[headerKey]);
		}
	}

	let options = {
		method: method,
		redirect: "follow",
		headers: _headers,
		credentials: "same-origin",
	};

	if (mode) {
		options.mode = mode;
	}

	if (data) {
		if (_headers.get("content-type") === "application/json") {
			options.body = JSON.stringify(data);
		} else {
			options.body = data;
		}
	}

	return options;
}

export const defaultRequestOptions = {
	page: 1,
	perPage: 25,
	search: null,
	sort: "-created_on",
	filters: [{ not_tag: "automation_duplicate" }],
};

export const createDefaultResource = () => {
	const user = store.getState().global.user;
	const account = store.getState().global.account;

	return new Promise((resolve, reject) => {
		listSendersService(
			{
				accountId: account.id,
			},
			(data) => {
				if (data?.data?.length > 0) {
					resolve({ sender: data.data[0] });
				} else {
					reject();
				}
			},
			() => {
				reject();
			}
		);
	}).then((result) => {
		return new Promise((resolve, reject) => {
			createListService(
				{
					default_sender: { id: result.sender.id },
					name: isValidEmail(account.name)
						? I18n._translate("app-general-lists_default")
						: I18n._translate("app-general-lists_default_named", {
							name: account.name,
						}),
					language: user.language || "en_US",
				},
				(data) => {
					acceptListPolicyService({
						id: data.data.id,
					});

					resolve({ ...result, list: data.data });
				},
				() => {
					reject();
				}
			);
		});
	});
};

export const getAmplitudeMonthlyStats = (account, callback) => {
	let last30Days = moment().subtract(1, "months").unix();
	let last7Days = moment().subtract(1, "weeks").unix();
	let last24Hours = moment().subtract(1, "days").unix();

	function getWantedStats(data) {
		let finalResults = {};
		if (data?.month) {
			const monthOpen =
				data.month.open_rate > 0
					? (data.month.sent_emails * data.month.open_rate) / 100
					: 0;
			const monthClick =
				monthOpen > 0 ? (data.month.click_rate * monthOpen) / 100 : 0;
			finalResults.month = {
				sents: data.month.sent_emails,
				opens: parseFloat(monthOpen.toFixed(2)),
				click: parseFloat(monthClick.toFixed(2)),
			};
		}
		if (data?.week) {
			const weekOpen =
				data.week.open_rate > 0
					? (data.week.sent_emails * data.week.open_rate) / 100
					: 0;
			const weekClick =
				weekOpen > 0 ? (data.week.click_rate * weekOpen) / 100 : 0;
			finalResults.week = {
				sents: data.week.sent_emails,
				opens: parseFloat(weekOpen.toFixed(2)),
				click: parseFloat(weekClick.toFixed(2)),
			};
		}
		if (data?.day) {
			const dayOpen =
				data.day.open_rate > 0
					? (data.day.sent_emails * data.day.open_rate) / 100
					: 0;
			const dayClick = dayOpen > 0 ? (data.day.click_rate * dayOpen) / 100 : 0;
			finalResults.day = {
				sents: data.day.sent_emails,
				opens: parseFloat(dayOpen.toFixed(2)),
				click: parseFloat(dayClick.toFixed(2)),
			};
		}

		return finalResults;
	}

	return new Promise((resolve, reject) => {
		getAccountStatsService(
			{ startTime: last30Days },
			(data) => {
				resolve({ month: data.data });
			},
			() => {
				reject();
			}
		);
	})
		.then((result) => {
			return new Promise((resolve, reject) => {
				getAccountStatsService(
					{ startTime: last7Days },
					(data) => {
						resolve({ ...result, week: data.data });
					},
					() => {
						reject(result);
					}
				);
			});
		})
		.then((result) => {
			return new Promise((resolve, reject) => {
				getAccountStatsService(
					{ startTime: last24Hours },
					(data) => {
						resolve({ ...result, day: data.data });
					},
					() => {
						reject(result);
					}
				);
			});
		})
		.then((result) => {
			callback(getWantedStats(result));
		})
		.catch((result) => {
			callback(getWantedStats(result));
		});
};

export function isPreviewingBrand() {
	return store.getState()?.global?.previewingBrand;
}

export function isPartner(checkPreview = false, options = {}) {
	const previewingBrand = isPreviewingBrand();
	const account = options?.currentAccount || store.getState()?.global?.account;
	if (checkPreview && previewingBrand) {
		setTimeout(() => {
			DialogHandler.show({
				type: "warning",
				title: <Translate value={"app-reseller-action-blocked"} />,
			});

			if (options?.btnRef?.current) {
				options?.btnRef?.current.reset();
			}
			if (options?.hideDialog) {
				DialogHandler.hide();
			}
			if (options?.hideFullPage) {
				FullPageLoaderHandler.hide();
			}
		}, 500);
		return account?.partner && previewingBrand;
	}
	return account?.partner && !previewingBrand;
}

export function handlePaginationSorting(data, perPage, page, sort) {
	const sortedData = [...data];
	switch (sort) {
		case "created_on":
		case "+created_on":
			sortedData.sort((a, b) => Number(a.created_on) - Number(b.created_on));
			break;
		case "-created_on":
			sortedData.sort((a, b) => Number(b.created_on) - Number(a.created_on));
			break;
		case "committed_on":
		case "+committed_on":
			sortedData.sort(
				(a, b) => Number(a.committed_on) - Number(b.committed_on)
			);
			break;
		case "-committed_on":
			sortedData.sort(
				(a, b) => Number(b.committed_on) - Number(a.committed_on)
			);
			break;
		case "+name": {
			sortedData.sort(function (a, b) {
				var nameA = a.name.toLowerCase(),
					nameB = b.name.toLowerCase();
				if (nameA < nameB) return -1;
				if (nameA > nameB) return 1;
				return 0;
			});
			break;
		}
		default:
			break;
	}
	var from = (page - 1) * Number(perPage);
	var to = from + Number(perPage);
	return sortedData.slice(from, to);
}

export function chameau(options = {}) {
	const camelToSnakeCase = (str) =>
		str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
	const out = {};
	for (const key in options) {
		out[camelToSnakeCase(key)] = options[key];
	}
	return out;
}


export function popImpersonificationTree() {
	const impersonationTree = getImpersonificationTree();
	impersonationTree.pop();
	setImpersonificationTree(impersonationTree);
}

export function amIImpersonating(returnId = false) {
	const impersonationTree = getImpersonificationTree();
	const impersonatingUser = store.getState()?.global?.impersonatingUser;

	if (returnId)
		return impersonatingUser ? store.getState()?.global.account.id : impersonationTree[impersonationTree.length - 1];
	return impersonationTree.length > 0 || impersonatingUser;
}

export function addToImpersonificationTree(id) {
	const impersonationTree = getImpersonificationTree();
	impersonationTree.push(id);
	setImpersonificationTree(impersonationTree);
}

export function removeImpersonificationTree() {
	window.sessionStorage.removeItem("impersonificationTree");
}

export function getImpersonificationTree() {
	return getLocalStorage("impersonificationTree", null, true) || [];
}

export function setImpersonificationTree(ids) {
	setLocalStorage("impersonificationTree", ids, null, true);
}

export function isPartnerOrOrganization(currentAccount, options = {}) {
	const account = currentAccount || store.getState().global?.account;
	return isPartner(options.checkPreview || false, { currentAccount: account, hideDialog: true }) || account?.organization;
}

export function isOrganization(currentAccount) {
	const account = currentAccount || store.getState().global?.account;
	return account?.organization;
}